import React, { ReactElement, useEffect, useState } from 'react'

import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/Breadcrumps'
import '@/styles/breadcrumbs.scss'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

const breadcrumbsMap: Record<string, { label: string }> = {
	services: {
		label: 'Услуги'
	},
	development: {
		label: 'Разработка ИТ-решений'
	},
	fintech: {
		label: 'Финтех-решения'
	},
	['ux-ui']: {
		label: 'UX/UI'
	},
	integrations: {
		label: 'Интеграции'
	},
	support: {
		label: 'Техническое сопровождение'
	},
	outstaffing: {
		label: 'Аутстаффинг'
	},
	'competition-sheet-system': {
		label: 'Система управления конкурентными листами'
	},
	b2b: {
		label: 'B2B: платежи от юрлиц и ИП'
	},
	b2c: {
		label: 'B2C: выплаты физлицам'
	},
	promotion: {
		label: 'Продвижение'
	},
	articles: {
		label: 'Блог'
	},
	cases: {
		label: 'Кейсы'
	},
	portfolio: {
		label: 'Кейсы'
	},
	'new-cases': {
		label: 'Новые Кейсы'
	},
	contacts: {
		label: 'Контакты'
	},
	'it-solutions': {
		label: 'ИТ-продукты'
	},
	'complex-solutions': {
		label: 'Комплексные решения'
	},
	'web-apps': {
		label: 'Сайты и приложения'
	}
}


const cases: Record<string, { title: string }> = {
	// DISABLED:
	// 'lichnyj-kabinetdlja-agregacii-zakazov-v-selskohozjajstvennoj-otrasli': {
	// 	title: 'Личный кабинет для агрегации заказов в сельскохозяйственной отрасли'
	// },
	'bajkalskiy-gazobeton-smm-audit': {
		title: 'Дизайн-аудит маркетинговых каналов Байкальский газобетон'
	},
	'shkola-vkusa-prjanosti-sladosti': {
		title: 'Разработка бренда кулинарной школы'
	},
	// DISABLED:
	// 'sozdanie-ai-reshenija-s-ispolzovaniem-chatgpt-dlja-kiwi-immigration': {
	// 	title: 'ИИ-решение для Kiwi Immigration на базе ChatGPT'
	// },
	'cifrovaja-transformacija-zavoda-gusenichnoj-tehniki': {
		title: 'Проект цифровизации для завода гусеничной техники'
	},
	'tsk-brigada': {
		title: 'Рекламная кампания для строительной компании'
	},
	'cifrovaja-transformacija-pishhevogo-proizvodstva': {
		title: 'Цифровизация пищевого цеха'
	},
	'cifrovizacija-krupnejshego-mukomolnogo-predprijatija-vostochnoj-sibiri': {
		title: 'Цифровая трансформация мукомольного завода'
	},
	'tulun-telekom': {
		title: 'Тулун-ТелеКом'
	},
	'money-roses': {
		title: 'SMM-стратегия цветочного бренда Money Roses'
	}
}

const portfolio: Record<string, { title: string }> = {
	'treasury-automation': {
		title: 'Автоматизация казначейства в финансовом секторе'
	},
	'materials-marketplace': {
		title: 'Онлайн маркетплейс материалов'
	},
	'multibanking': {
		title: 'Мультибанкинг: автоматизация в финтехе'
	},
	'partner-b2b': {
		title: 'B2B-платформа для оптовой торговли'
	},
	'agriculture-cabinet': {
		title: 'Личный кабинет для сельскохозяйственной отрасли'
	},
	'lk-agro-b2b': {
		title: 'Личный кабинет в сельском хозяйстве для B2B'
	},
	'card-project': {
		title: 'ИИ-решение для Kiwi Immigration на базе ChatGPT'
	}
}

// Функция для получения названия статьи из Strapi
const getArticleTitle = async (slug: string): Promise<string> => {
	try {
		// Используем относительный путь через Nginx на production
		const apiUrl = typeof window !== 'undefined' 
			? (process.env.NEXT_PUBLIC_STRAPI_URL || '/strapi/api')
			: (process.env.NEXT_PUBLIC_STRAPI_URL || process.env.NEXT_PUBLIC_URL ? `${process.env.NEXT_PUBLIC_URL || 'https://true-main-dev.tw1.ru'}/strapi/api` : 'http://localhost:1337')
		const url = apiUrl.includes('/strapi/api')
			? `${apiUrl}/articles?filters[slug][$eq]=${slug}&populate=*`
			: `${apiUrl}/api/articles?filters[slug][$eq]=${slug}&populate=*`
		const response = await fetch(url)
		if (!response.ok) {
			throw new Error('Failed to fetch article')
		}
		const data = await response.json()
		return data.data?.[0]?.title || 'Статья'
	} catch (error) {
		console.error('Error fetching article title:', error)
		return 'Статья'
	}
}

// Функция для получения названия кейса из Strapi
const getCasePageTitle = async (slug: string): Promise<string> => {
	try {
		const apiUrl = typeof window !== 'undefined' 
			? (process.env.NEXT_PUBLIC_STRAPI_URL || '/strapi/api')
			: (process.env.NEXT_PUBLIC_STRAPI_URL || process.env.NEXT_PUBLIC_URL ? `${process.env.NEXT_PUBLIC_URL || 'https://true-main-dev.tw1.ru'}/strapi/api` : 'http://localhost:1337')
		const baseUrl = apiUrl.includes('/strapi/api')
			? apiUrl
			: apiUrl === 'http://localhost:1337'
			? `${apiUrl}/api`
			: `${apiUrl}/api`
		
		const url = `${baseUrl}/case-pages?filters[slug][$eq]=${slug}&fields[0]=headline`
		const response = await fetch(url)
		
		if (!response.ok) {
			throw new Error('Failed to fetch case page')
		}
		
		const data = await response.json()
		const headline = data.data?.[0]?.headline
		
		if (!headline) {
			return 'Кейс'
		}
		
		// Убираем HTML теги из headline для breadcrumbs
		const textOnly = headline.replace(/<[^>]*>/g, '').trim()
		return textOnly || 'Кейс'
	} catch (error) {
		return 'Кейс'
	}
}

export const Breadcrumbs: React.FC<React.HTMLAttributes<HTMLElement>> = ({ className }) => {
	const pathname = usePathname()
	const routes = pathname
		.split('/')
		.slice(1)
		.filter(path => path.length > 0)

	const [articleTitle, setArticleTitle] = useState<string>('Статья')
	const [casePageTitle, setCasePageTitle] = useState<string>('Кейс')

	// Получаем название статьи из Strapi, если это страница статьи
	useEffect(() => {
		const isArticlePage = routes.length === 2 && routes[0] === 'articles'
		if (isArticlePage) {
			const articleSlug = routes[1]
			getArticleTitle(articleSlug).then(title => {
				setArticleTitle(title)
			})
		}
	}, [pathname])

	// Получаем название кейса из Strapi, если это страница кейса из portfolio
	useEffect(() => {
		const isCasePage = routes.length === 2 && routes[0] === 'portfolio'
		if (isCasePage) {
			const caseSlug = routes[1]
			// Проверяем, есть ли кейс в старом маппинге portfolio
			if (!portfolio[caseSlug]) {
				// Если нет в старом маппинге, пытаемся получить из Strapi
				getCasePageTitle(caseSlug).then(title => {
					setCasePageTitle(title)
				})
			}
		}
	}, [pathname])

	let fullHref: string | undefined = undefined
	const breadcrumbItems: ReactElement[] = []
	let breadcrumbPage: ReactElement = <></>

	for (let i = 0; i < routes.length; i++) {
		const route = routes[i]
		const prevRoute = routes[i - 1]
		let href

		href = fullHref ? `${fullHref}/${route}` : `/${route}`
		fullHref = href

		if (i === routes.length - 1) {
			breadcrumbPage = (
				<BreadcrumbItem className={'breadcrumbs__item'}>
					<BreadcrumbPage className={'breadcrumbs__current-page'}>
						{prevRoute === 'articles'
							? articleTitle
							: prevRoute === 'cases'
								? cases[route]?.title || 'Кейс'
								: prevRoute === 'portfolio'
									? portfolio[route]?.title || casePageTitle
									: (breadcrumbsMap[route]?.label ?? route)}
					</BreadcrumbPage>
				</BreadcrumbItem>
			)
		} else {
			breadcrumbItems.push(
				<React.Fragment key={href}>
					<BreadcrumbSeparator className={'breadcrumbs__separator'}>/</BreadcrumbSeparator>
					<BreadcrumbItem className={'breadcrumbs__item'}>
						<BreadcrumbLink asChild>
							<Link href={route === 'portfolio' ? '/cases/' : href}>
								{breadcrumbsMap[route]?.label ?? route}
							</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
				</React.Fragment>
			)
		}
	}

	return (
		<Breadcrumb className={`${className}`}>
			<BreadcrumbList>
				<BreadcrumbItem className={'breadcrumbs__item'}>
					<BreadcrumbLink asChild>
						<Link href='/'>Главная</Link>
					</BreadcrumbLink>
				</BreadcrumbItem>
				{breadcrumbItems}
				<BreadcrumbSeparator>/</BreadcrumbSeparator>
				{breadcrumbPage}
			</BreadcrumbList>
		</Breadcrumb>
	)
}
